/**
 * Terrain shading preset (issue #106): `stylised` keeps today's deliberate
 * flat-shaded look; `natural` renders smooth vertex normals and (low tier
 * only) swaps the low-tier hard elevation bands for a continuous colour ramp
 * sourced from the same terrainAlbedo() the high-tier splat shader already
 * uses (issue #90 "no hue drift" doctrine — see terrain/terrainMaterial.ts).
 * Orthogonal to the rendering quality tier (core/quality.ts): every tier x
 * preset combination is valid.
 *
 * Same URL/storage/default resolution shape as core/quality.ts, kept as a
 * separate module because the two axes are independent user choices with
 * independent defaults — quality defaults to hardware auto-detection, this
 * one defaults unconditionally to natural, and there is no "auto" concept
 * here. Pure and dependency-free so it is unit-tested in Node; main.ts reads
 * `location`/`localStorage` and applies the result.
 */
export type ShadingPreset = 'stylised' | 'natural';

function isShadingPreset(value: unknown): value is ShadingPreset {
  return value === 'stylised' || value === 'natural';
}

/**
 * Reads `?shading=stylised|natural` from a location search string.
 * `stylized` is accepted as a spelling alias for `stylised`. Any other value
 * (including a missing or empty param) is ignored, never thrown — the
 * "unrecognised value" predictable-failure-case: falls back to the saved
 * choice, then the default.
 */
export function parseShadingOverride(search: string): ShadingPreset | undefined {
  const value = new URLSearchParams(search).get('shading');
  if (value === 'stylized') return 'stylised';
  return isShadingPreset(value) ? value : undefined;
}

/** localStorage key used for the persisted manual shading preset. */
export const SHADING_STORAGE_KEY = 'ray2.shadingPreset';

/**
 * Storage-like interface for persisting a user's manual shading choice.
 * Deliberately narrow (get/set only) and injected rather than imported so
 * this module never touches `window.localStorage` directly and stays
 * Node-testable. Both methods may throw (e.g. `SecurityError` in private
 * browsing); callers of the helpers below must not need to catch — the
 * helpers absorb that themselves.
 */
export interface ShadingStorage {
  get(key: string): string | null | undefined;
  set(key: string, value: string): void;
}

/**
 * Reads the saved shading preset, if any. Returns undefined (never throws)
 * when nothing is saved, the saved value doesn't parse (corrupted, or from
 * some future version we don't recognise), or storage access itself throws —
 * all three are treated identically as "no saved choice".
 */
export function loadSavedShadingPreset(storage: ShadingStorage): ShadingPreset | undefined {
  try {
    const raw = storage.get(SHADING_STORAGE_KEY);
    return isShadingPreset(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persists a manually-chosen shading preset. Returns true on success, false
 * if storage access threw (e.g. private browsing) — this never throws itself:
 * a failed save silently no-ops (the switch still applies live for the
 * session).
 */
export function saveShadingPreset(storage: ShadingStorage, preset: ShadingPreset): boolean {
  try {
    storage.set(SHADING_STORAGE_KEY, preset);
    return true;
  } catch {
    return false;
  }
}

/** Default preset when neither a URL override nor a persisted choice applies. */
export const DEFAULT_SHADING_PRESET: ShadingPreset = 'natural';

/**
 * Full precedence resolver (issue #106): URL `?shading=` beats a saved user
 * choice, which beats the natural default — the same order as
 * resolveQualityTier, minus the auto-detect step (there is no hardware
 * signal for this axis).
 *
 * Deliberately does not persist the URL override: a shared link carrying
 * `?shading=stylised` must only affect that session, not permanently change
 * whoever opens it (the caller decides separately whether/when to call
 * {@link saveShadingPreset}, e.g. in response to an explicit UI choice).
 */
export function resolveShadingPreset(params: { search: string; storage: ShadingStorage }): ShadingPreset {
  const urlOverride = parseShadingOverride(params.search);
  if (urlOverride) return urlOverride;

  const saved = loadSavedShadingPreset(params.storage);
  if (saved) return saved;

  return DEFAULT_SHADING_PRESET;
}
